import React, { useState, useEffect, useCallback } from 'react';
import { rdvAPI, advisorsAPI } from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Rendez-vous — the appointments grid (replaces the Google Sheet).
// Spreadsheet-like: one row per appointment, filters by client/date/statut.
// Actions: add (+), check, edit (✏️), cancel-by-mistake (✋), and follow-up —
// "pas venu" → à rappeler with a date; the booking agent calls back.
// Polls every 20s so admin/advisor updates appear live.
// ─────────────────────────────────────────────────────────────────────────────

const STATUTS = {
  nouveau:   { label: 'Nouveau',     color: '#2563eb', bg: 'rgba(37,99,235,0.12)' },
  confirme:  { label: 'Confirmé',     color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  annule:    { label: 'Annulé',      color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  rencontre: { label: 'Rencontre',    color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' },
  no_show:   { label: 'No show',     color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  injoignable:{ label: 'Injoignable', color: '#f97316', bg: 'rgba(249,115,22,0.12)' },
  rdv2:      { label: '2ᵉ RDV',       color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  refus:     { label: 'Refusé',      color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
};

const PERIODES = [
  { key: 'today',     label: "Aujourd'hui" },
  { key: 'week',      label: 'Cette semaine' },
  { key: 'upcoming',  label: 'À venir' },
  { key: 'all',       label: 'Tout' },
  { key: 'followup',  label: 'À rappeler' },
];

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d.length === 10 ? d + 'T12:00:00' : d);
    return dt.toLocaleDateString('fr-CA', { day: '2-digit', month: 'short', weekday: 'short' });
  } catch { return d; }
}
function fmtTime(t) { return t ? String(t).slice(0, 5) : '—'; }
function fmtDT(dt) {
  if (!dt) return '';
  try { return new Date(dt).toLocaleString('fr-CA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return dt; }
}

const inputStyle = { padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: '100%' };

export default function Rendezvous({ user }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [counts, setCounts] = useState({ today: 0, due_followups: 0 });
  const [period, setPeriod] = useState('today');
  const [advisorId, setAdvisorId] = useState('');
  const [statut, setStatut] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);   // row being edited ({} = new)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');   // transient "✓ Enregistré"

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (period === 'today') params.period = 'today';
      else if (period === 'week') params.period = 'week';
      else if (period === 'upcoming') params.period = 'upcoming';
      else if (period === 'followup') params.followup = 'today';
      if (advisorId) params.advisor_id = advisorId;
      if (statut) params.statut = statut;
      if (q.trim()) { params.q = q.trim(); }
      const data = await rdvAPI.list(params);
      setRows(Array.isArray(data) ? data : []);
      setErr('');
    } catch (e) {
      setRows([]);
      setErr('Impossible de charger les rendez-vous — vérifiez la connexion.');
    }
    finally { setLoading(false); }
  }, [period, advisorId, statut, q]);

  const loadCount = useCallback(async () => {
    try { setCounts(await rdvAPI.counts()); } catch {}
  }, []);

  useEffect(() => { if (!user) return; loadCount(); const t = setInterval(loadCount, 30000); return () => clearInterval(t); }, [user, loadCount]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [open, load]);
  useEffect(() => { if (open) advisorsAPI.list().then(d => setAdvisors(Array.isArray(d) ? d : [])).catch(() => {}); }, [open]);

  const cancelError = async (r) => {
    if (!window.confirm(`Annuler le rendez-vous de ${r.client_first || ''} ${r.client_last || ''} (${fmtDate(r.rdv_date)}) ?\nÀ utiliser seulement en cas d'erreur de validation.`)) return;
    try { await rdvAPI.cancelError(r.id); flash('✓ Rendez-vous annulé'); load(); loadCount(); }
    catch (e) { alert('Impossible : ' + (e?.response?.data?.error || e?.message)); }
  };

  const markRetour = async (r, newStatut) => {
    let followup_at = null;
    let retour = r.retour || '';
    let payload = { statut: newStatut, retour, followup_at };
    if (newStatut === 'injoignable' || newStatut === 'rdv2') {
      const d = window.prompt(
        newStatut === 'injoignable'
          ? "Date du rappel par l'AGENT (AAAA-MM-JJ HH:MM, heure du Québec) :"
          : "Date du rappel par le CLIENT — 2ᵉ RDV (AAAA-MM-JJ HH:MM, heure du Québec) :", '');
      if (!d) return;
      followup_at = d;
      payload.followup_at = d;
      retour = window.prompt('Note (optionnel) :', retour) ?? retour;
      payload.retour = retour;
    } else if (newStatut === 'reporte') {
      const nd = window.prompt('RDV reporté — NOUVELLE date du rendez-vous (AAAA-MM-JJ) :', '');
      if (!nd) return;
      const nt = window.prompt('NOUVELLE heure (HH:MM) :', '');
      if (!nt) return;
      const note = window.prompt('Note (optionnel) :', retour) ?? retour;
      payload = { statut: 'reporte', retour: note, new_rdv_date: nd, new_rdv_time: nt };
    } else if (newStatut === 'no_show' || newStatut === 'rencontre' || newStatut === 'refus') {
      const msg = newStatut === 'rencontre' ? 'Résultat de la rencontre (optionnel) :'
        : newStatut === 'no_show' ? 'No show — note (optionnel) :'
        : 'Refus — note (optionnel) :';
      const note = window.prompt(msg, retour);
      if (note === null) return;
      retour = note;
      payload.retour = note;
    }
    try {
      await rdvAPI.retour(r.id, payload);
      flash('✓ Enregistré');
      load(); loadCount();
    } catch (e) { alert('Impossible : ' + (e?.response?.data?.error || e?.message)); }
  };

  const saveEdit = async () => {
    setSaving(true); setErr('');
    try {
      const b = edit;
      const payload = {
        advisor_id: b.advisor_id, client_first: b.client_first, client_last: b.client_last,
        phone: b.phone, email: b.email, ligne2: b.ligne2,
        rdv_date: b.rdv_date, rdv_time: b.rdv_time, mode: b.mode,
        statut: b.statut || 'nouveau', comments: b.comments,
        retour: b.retour, followup_at: b.followup_at || null,
      };
      if (b.id) await rdvAPI.update(b.id, payload);
      else await rdvAPI.create(payload);
      setEdit(null); flash('✓ Enregistré'); load(); loadCount();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'erreur');
    } finally { setSaving(false); }
  };

  // « Données collectées » — the call-time recap. Single source (2026-10-20):
  // the summary lives in rendezvous.comments; the legacy script_summary key on
  // the lead is the fallback for RDVs booked before.
  const collectedData = (r) => {
    if (r.comments) return r.comments;
    try {
      const cf = typeof r.lead_custom_fields === 'string'
        ? JSON.parse(r.lead_custom_fields || '{}') : (r.lead_custom_fields || {});
      return cf.script_summary || null;
    } catch { return null; }
  };
  const [detail, setDetail] = useState(null);

  const badge = (s) => {
    const m = STATUTS[s] || STATUTS.nouveau;
    return <span style={{ padding: '2px 8px', borderRadius: 100, fontSize: 11, fontWeight: 800, color: m.color, background: m.bg, border: `1.5px solid ${m.color}` }}>{m.label}</span>;
  };

  return (
    <>
      {/* Left-panel trigger */}
      <button type="button" className="ctrl" onClick={() => setOpen(true)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Rendez-vous</span>
        {(counts.today > 0 || counts.due_followups > 0) && (
          <span style={{ display: 'flex', gap: 4 }}>
            {counts.today > 0 && <span style={{ background: '#10b981', color: '#fff', borderRadius: 100, padding: '1px 7px', fontSize: 11, fontWeight: 900 }}>{counts.today}</span>}
            {counts.due_followups > 0 && <span style={{ background: '#f97316', color: '#fff', borderRadius: 100, padding: '1px 7px', fontSize: 11, fontWeight: 900 }}>{counts.due_followups} à rappeler</span>}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', justifyContent: 'flex-end' }}
          onClick={e => e.target === e.currentTarget && setOpen(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(30,18,72,0.35)', backdropFilter: 'blur(4px)' }} onClick={() => setOpen(false)} />
          <style>{`
            @keyframes bkSlideIn{from{transform:translateX(40px);opacity:0}to{transform:none;opacity:1}}
            @media (prefers-reduced-motion: reduce){ * { animation: none !important; transition: none !important; } }
            .rdv-act:focus-visible, .rdv-panel button:focus-visible, .rdv-panel select:focus-visible, .rdv-panel input:focus-visible { outline: 2.5px solid #7c3aed; outline-offset: 1px; }
          `}</style>
          <div style={{ position: 'relative', zIndex: 1, width: '94vw', maxWidth: 1150, height: '100vh', background: '#fff', boxShadow: '-12px 0 48px rgba(124,58,237,0.2)', display: 'flex', flexDirection: 'column', animation: 'bkSlideIn 0.25s ease' }}>
            {/* Header + filters */}
            <div style={{ padding: '16px 20px 12px', background: 'linear-gradient(135deg,#7c3aed,#6366f1)', color: '#fff', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 900 }}>Rendez-vous</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setEdit({ mode: 'telephonique', statut: 'nouveau' })}
                    style={{ background: '#10b981', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 800, color: '#fff', cursor: 'pointer' }}>+ Ajouter</button>
                  <button type="button" onClick={() => setOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: '#fff' }}>✕</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {PERIODES.map(p => (
                  <button type="button" key={p.key} onClick={() => setPeriod(p.key)} style={{
                    padding: '5px 12px', borderRadius: 100, cursor: 'pointer', fontSize: 11, fontWeight: 800,
                    border: '2px solid ' + (period === p.key ? '#fff' : 'rgba(255,255,255,0.3)'),
                    background: period === p.key ? '#fff' : 'transparent',
                    color: period === p.key ? '#7c3aed' : '#fff'
                  }}>{p.label}</button>
                ))}
                <select value={advisorId} onChange={e => setAdvisorId(e.target.value)}
                  style={{ marginLeft: 8, padding: '5px 8px', borderRadius: 100, fontSize: 11, fontWeight: 700, border: '2px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: '#fff' }}>
                  <option value="" style={{ color: '#222' }}>Tous les clients</option>
                  {advisors.map(a => <option key={a.id} value={a.id} style={{ color: '#222' }}>{a.name}</option>)}
                </select>
                <select value={statut} onChange={e => setStatut(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 100, fontSize: 11, fontWeight: 700, border: '2px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: '#fff' }}>
                  <option value="" style={{ color: '#222' }}>Tous statuts</option>
                  {Object.entries(STATUTS).map(([k, m]) => <option key={k} value={k} style={{ color: '#222' }}>{m.label}</option>)}
                </select>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher nom / tel…"
                  style={{ padding: '5px 10px', borderRadius: 100, fontSize: 11, border: '2px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: '#fff', width: 150 }} />
              </div>
            </div>

            {/* Grid */}
            <div className="rdv-panel" style={{ flex: 1, overflow: 'auto', background: '#f7f7fb', position: 'relative' }}>
              {err && (
                <div role="alert" style={{ margin: 12, padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1.5px solid #ef4444', color: '#b91c1c', fontSize: 12.5, fontWeight: 700 }}>
                  Erreur : {err} <button type="button" onClick={load} style={{ marginLeft: 8, border: 'none', background: 'transparent', color: '#b91c1c', fontWeight: 800, cursor: 'pointer', textDecoration: 'underline' }}>Réessayer</button>
                </div>
              )}
              {toast && (
                <div role="status" style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', background: '#10b981', color: '#fff', fontWeight: 800, fontSize: 13, borderRadius: 999, padding: '8px 18px', boxShadow: '0 4px 16px rgba(16,185,129,0.4)', zIndex: 5 }}>
                  {toast}
                </div>
              )}
              {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Chargement…</div>
              ) : rows.length === 0 ? (
                <div style={{ padding: 50, textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 10 }}>Aucun rendez-vous</div>
                  <div style={{ fontWeight: 800, color: '#333' }}>Aucun rendez-vous</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Les RDV confirmés dans le script apparaissent ici automatiquement.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, background: '#fff', minWidth: 980 }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: '#eef0f7', textAlign: 'left' }}>
                      {['Appel', 'Agent', 'Prospect', 'Téléphone', 'Courriel', 'RDV', 'Heure', 'Client', 'Mode', 'Statut', 'Commentaires / suivi', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', fontSize: 10.5, fontWeight: 900, textTransform: 'uppercase', color: '#475569', borderBottom: '2px solid #dde' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const due = r.followup_at && new Date(r.followup_at) <= new Date() && (r.statut === 'injoignable' || r.statut === 'rdv2');
                      const collected = collectedData(r);
                      return (
                        <tr key={r.id} style={{ borderBottom: '1px solid #eef', background: r.statut === 'annule' ? 'rgba(239,68,68,0.04)' : due ? 'rgba(249,115,22,0.07)' : '#fff' }}>
                          <td style={{ padding: '7px 10px', fontSize: 11.5, color: '#475569', whiteSpace: 'nowrap' }}>{r.created_at ? fmtDT(r.created_at) : '—'}</td>
                          <td style={{ padding: '7px 10px', fontSize: 11.5, color: '#475569' }}>{r.agent_name || r.agent_username || '—'}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 700 }}>{[r.client_first, r.client_last].filter(Boolean).join(' ') || '—'}</td>
                          <td style={{ padding: '7px 10px', fontFamily: 'var(--mono)' }}>{r.phone || '—'}</td>
                          <td style={{ padding: '7px 10px', fontSize: 11.5, color: '#475569' }}>{r.email || '—'}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 700 }}>{fmtDate(r.rdv_date)}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 700, color: '#7c3aed' }}>{fmtTime(r.rdv_time)}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 700 }}>{r.advisor_name || '—'}</td>
                          <td style={{ padding: '7px 10px', fontSize: 11.5 }}>{r.mode === 'virtuel' ? 'Virtuel' : r.mode === 'telephonique' ? 'Téléphonique' : '—'}</td>
                          <td style={{ padding: '7px 10px' }}>{badge(r.statut)}{due && <div style={{ fontSize: 10, color: '#f97316', fontWeight: 800, marginTop: 2 }}>À rappeler : {fmtDT(r.followup_at)}</div>}</td>
                          <td style={{ padding: '7px 10px', fontSize: 11.5, color: '#475569', maxWidth: 180 }}>
                            {r.comments && <div>{r.comments}</div>}
                            {r.retour && <div style={{ color: '#0369a1' }}>↩︎ {r.retour}</div>}
                            {r.followup_at && (r.statut === 'injoignable' || r.statut === 'rdv2') && (
                              <div style={{ color: '#f97316', fontWeight: 700 }}>
                                {r.statut === 'injoignable' ? 'Agent rappelle' : 'Client rappelle'} : {fmtDT(r.followup_at)}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                            <button type="button" className="rdv-act" title="Modifier" onClick={() => setEdit({ ...r })} style={{ cursor: 'pointer', border: '1px solid #ddd', background: '#f9f9fb', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>Modifier</button>
                            {' '}
                            <button type="button" className="rdv-act" title="Données collectées (fiche d'appel)" disabled={!collected} onClick={() => setDetail(r)} style={{ cursor: collected ? 'pointer' : 'not-allowed', border: '1px solid #c4b5fd', background: '#f5f3ff', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700, opacity: collected ? 1 : 0.35 }}>Données</button>
                            {' '}
                            <button type="button" className="rdv-act" title="Rencontre (terminé)" onClick={() => markRetour(r, 'rencontre')} style={{ cursor: 'pointer', border: '1px solid #93c5fd', background: '#eff6ff', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>Rencontre</button>
                            {' '}
                            <button type="button" className="rdv-act" title="No show" onClick={() => markRetour(r, 'no_show')} style={{ cursor: 'pointer', border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>No show</button>
                            {' '}
                            <button type="button" className="rdv-act" title="Injoignable → agent rappelle" onClick={() => markRetour(r, 'injoignable')} style={{ cursor: 'pointer', border: '1px solid #fdba74', background: '#fff7ed', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>Injoignable</button>
                            {' '}
                            <button type="button" className="rdv-act" title="2ᵉ RDV → client rappelle" onClick={() => markRetour(r, 'rdv2')} style={{ cursor: 'pointer', border: '1px solid #d8b4fe', background: '#faf5ff', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>2ᵉ RDV</button>
                          <button type="button" title="Refus" onClick={() => markRetour(r, 'refus')} style={{ cursor: 'pointer', border: '1px solid #94a3b8', background: '#f1f5f9', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>Refus</button>
                          <button type="button" title="RDV reporté — nouvelle date/heure" onClick={() => markRetour(r, 'reporte')} style={{ cursor: 'pointer', border: '1px solid #7c3aed', background: '#f5f3ff', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>Reporté</button>
                            {' '}
                            <button type="button" className="rdv-act" title="Annuler (validation par erreur)" onClick={() => cancelError(r)} style={{ cursor: 'pointer', border: '1px solid #fca5a5', background: '#fef2f2', borderRadius: 6, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}>Annuler</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Données collectées — fiche d'appel complète (réponses du script) */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(30,18,72,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setDetail(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 22, width: 560, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4, color: '#7c3aed' }}>Données collectées — {[detail.client_first, detail.client_last].filter(Boolean).join(' ')}</div>
            <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 12 }}>
              Appel : {detail.created_at ? fmtDT(detail.created_at) : '—'} · Agent : {detail.agent_name || '—'}
            </div>
            <div style={{ background: '#f8f7fc', border: '1px solid #e4e1f5', borderRadius: 10, padding: 14, fontSize: 12.5, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
              {(collectedData(detail) || 'Aucune donnée collectée sur la fiche de ce prospect.').split(' | ').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" onClick={() => setDetail(null)} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #ddd', background: '#f9f9fb', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit / add modal */}
      {edit && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(30,18,72,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setEdit(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 22, width: 480, maxWidth: '95vw', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 17, fontWeight: 900, marginBottom: 14, color: '#7c3aed' }}>{edit.id ? 'Modifier le rendez-vous' : '+ Nouveau rendez-vous'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Prénom<input style={inputStyle} value={edit.client_first || ''} onChange={e => setEdit({ ...edit, client_first: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Nom<input style={inputStyle} value={edit.client_last || ''} onChange={e => setEdit({ ...edit, client_last: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Téléphone<input style={inputStyle} value={edit.phone || ''} onChange={e => setEdit({ ...edit, phone: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Courriel<input style={inputStyle} value={edit.email || ''} onChange={e => setEdit({ ...edit, email: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Date RDV<input type="date" style={inputStyle} value={edit.rdv_date ? String(edit.rdv_date).slice(0, 10) : ''} onChange={e => setEdit({ ...edit, rdv_date: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Heure<input type="time" style={inputStyle} value={edit.rdv_time ? String(edit.rdv_time).slice(0, 5) : ''} onChange={e => setEdit({ ...edit, rdv_time: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Client
                <select style={inputStyle} value={edit.advisor_id || ''} onChange={e => setEdit({ ...edit, advisor_id: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {advisors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Mode
                <select style={inputStyle} value={edit.mode || ''} onChange={e => setEdit({ ...edit, mode: e.target.value })}>
                  <option value="telephonique">Téléphonique</option>
                  <option value="virtuel">Virtuel (Zoom/Teams)</option>
                </select>
              </label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Statut
                <select style={inputStyle} value={edit.statut || 'nouveau'} onChange={e => setEdit({ ...edit, statut: e.target.value })}>
                  {Object.entries(STATUTS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Ligne 2<input style={inputStyle} value={edit.ligne2 || ''} onChange={e => setEdit({ ...edit, ligne2: e.target.value })} /></label>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#475569' }}>Rappel (AAAA-MM-JJ HH:MM, heure du Québec)
                <input style={inputStyle} placeholder="ex : 2026-10-15 14:30" value={edit.followup_at ? String(edit.followup_at).slice(0, 16).replace('T', ' ') : ''} onChange={e => setEdit({ ...edit, followup_at: e.target.value })} />
              </label>
              <label style={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 800, color: '#475569' }}>Commentaires (agent)<input style={inputStyle} value={edit.comments || ''} onChange={e => setEdit({ ...edit, comments: e.target.value })} /></label>
              <label style={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 800, color: '#0369a1' }}>Retour du client<input style={inputStyle} value={edit.retour || ''} onChange={e => setEdit({ ...edit, retour: e.target.value })} /></label>
            </div>
            {err && <div style={{ color: '#ef4444', fontSize: 12, fontWeight: 700, marginTop: 8 }}>Erreur : {err}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={saveEdit} disabled={saving}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                {saving ? 'Enregistrement…' : edit.id ? 'Enregistrer' : 'Ajouter le rendez-vous'}
              </button>
              <button type="button" onClick={() => setEdit(null)} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#f9f9fb', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
